import { __rest } from "tslib";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Banner, Box, ButtonAlpha, LinkButton, StackBlock, TextAlpha, TextField } from '@yonderland/andes-react';
import { useAemConfig } from '@as-react/common-components';
import { customerMutations } from '@as-react/common-queries';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, FormProvider } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { useMutation } from '@tanstack/react-query';
import { FormField } from '../../../form/FormField';
import { I18N_NAMESPACES } from '../../../../constants/i18nConstants';
import FormValidationUtil from '../../../../util/FormValidationUtil';
import { useCheckoutState } from '../../../../context/CheckoutContext';
export const ForgotPasswordForm = ({ onBackButtonClick }) => {
    const { t } = useTranslation([
        I18N_NAMESPACES.account,
        I18N_NAMESPACES.checkout,
        I18N_NAMESPACES.general,
        I18N_NAMESPACES.navigation,
    ]);
    const [errorFeedback, setErrorFeedback] = useState('');
    const [resetPasswordEmailSent, setResetPasswordEmailSent] = useState(false);
    const { defaultRequestParams, servicesEndpoint, mainWebShop } = useAemConfig();
    const [state, dispatch] = useCheckoutState();
    const { email } = state.login;
    const validationSchema = Yup.object({
        email: Yup.string()
            .required(t(`${I18N_NAMESPACES.account}:delivery.required`))
            .matches(FormValidationUtil.emailPatternRegex, t(`${I18N_NAMESPACES.general}:email.error.invalid`)),
    });
    const formProps = useForm({
        defaultValues: { email },
        mode: 'onTouched',
        resolver: yupResolver(validationSchema),
    });
    const { sendPasswordResetEmailMutation } = customerMutations({ servicesEndpoint, defaultRequestParams });
    const { mutate: sendPasswordResetEmail, isPending } = useMutation(sendPasswordResetEmailMutation());
    const handleSubmit = ({ email }) => {
        sendPasswordResetEmail({ email }, {
            onSuccess: () => {
                setErrorFeedback('');
                setResetPasswordEmailSent(true);
                dispatch({ type: 'update_login_data', payload: { email } });
            },
            onError: error => {
                var _a, _b;
                const errorMessage = ((_b = (_a = error.response) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.error_code) === '404'
                    ? `${I18N_NAMESPACES.navigation}:flyout.account.error.email.unknown.user`
                    : `${I18N_NAMESPACES.navigation}:flyout.account.error.operation.mail`;
                setErrorFeedback(t(errorMessage));
            },
        });
    };
    return (_jsx(FormProvider, Object.assign({}, formProps, { children: _jsx("form", { onSubmit: formProps.handleSubmit(handleSubmit), name: "forgot-password-form", children: _jsxs(StackBlock, { gap: "6", children: [_jsxs("div", { children: [_jsx(TextAlpha, { variant: "headingS", color: "emphasised", children: t(`${I18N_NAMESPACES.checkout}:checkout.password_forgotten.title`) }), !resetPasswordEmailSent && (_jsx(Box, { paddingBlockStart: "3", children: _jsx(TextAlpha, { children: t([
                                        `${I18N_NAMESPACES.checkout}:checkout.password_forgotten.intro.${mainWebShop}`,
                                        `${I18N_NAMESPACES.checkout}:checkout.password_forgotten.intro`,
                                    ]) }) }))] }), errorFeedback && _jsx(Banner, { text: errorFeedback, status: "error" }), resetPasswordEmailSent && (_jsx(Banner, { text: t([
                            `${I18N_NAMESPACES.checkout}:checkout.password_forgotten.success.${mainWebShop}`,
                            `${I18N_NAMESPACES.checkout}:checkout.password_forgotten.success`,
                        ]), status: "success" })), !resetPasswordEmailSent && (_jsxs(_Fragment, { children: [_jsx(FormField, { name: "email", children: (_a) => {
                                    var _b = _a.fieldProps, { ref } = _b, options = __rest(_b, ["ref"]), { error } = _a;
                                    return (_jsx(TextField, Object.assign({}, options, { label: t(`${I18N_NAMESPACES.checkout}:checkout.email_recognition.email.label`), name: "email", id: "email", autoComplete: "email", type: "email", error: error, testId: "forgot-password-form-email" })));
                                } }), _jsx(ButtonAlpha, { type: "submit", theme: "branded", state: isPending ? 'loading' : 'interactive', text: t(`${I18N_NAMESPACES.checkout}:checkout.password_forgotten.action`), fullWidth: true, testId: "forgot-password-form-submit" }), _jsxs(TextAlpha, { alignment: "center", children: [t([
                                        `${I18N_NAMESPACES.checkout}:checkout.password_forgotten.return_back.label.${mainWebShop}`,
                                        `${I18N_NAMESPACES.checkout}:checkout.password_forgotten.return_back.label`,
                                    ]), ' ', _jsx(LinkButton, { onClick: onBackButtonClick, text: _jsx(TextAlpha, { alignment: "center", as: "span", children: t(`${I18N_NAMESPACES.checkout}:checkout.password_forgotten.return_back.action`) }) })] })] }))] }) }) })));
};
